// @file Video-recorder-specific state for the combined AV recorder.
// Extracted from useContentPickerAvRecorderStore to keep concerns separated.
// Dependencies: useGlobalContentPickerStore, useGlobalConfirmationDialogStore (no dependency on the AV store).

import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { ContentPickerSource } from '@@/bits/content_picker'
import type { VideoRecorderPickerState } from '@@/bits/content_picker_configs'
import { VideoRecorderStatus } from '@@/bits/content_picker_configs'
import { __ } from '@@/bits/intl'
import { unboundedWatch } from '@@/bits/vue'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalContentPickerStore } from '@@/pinia/useGlobalContentPickerStore'
import { defineStore } from 'pinia'
import { computed } from 'vue'

const useContentPickerAvVideoRecorderStore = defineStore('contentPickerAvVideoRecorderStore', () => {
  const globalStore = useGlobalContentPickerStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()

  // #region Video recorder state

  function setVideoRecorderStatus(value: VideoRecorderStatus): void {
    globalStore.setPickerState(ContentPickerSource.VideoRecorder, {
      source: ContentPickerSource.VideoRecorder,
      videoRecorderStatus: value,
    })
  }

  const videoRecorderStatus = computed<VideoRecorderStatus>(() => {
    const state = globalStore.currentSession?.pickerStates[ContentPickerSource.VideoRecorder]
    if (state != null && 'videoRecorderStatus' in state) {
      return (state as VideoRecorderPickerState).videoRecorderStatus
    }
    return VideoRecorderStatus.Connecting
  })

  const hasUnsavedVideoWork = computed<boolean>(() => {
    const s = videoRecorderStatus.value
    return (
      s === VideoRecorderStatus.Recording || s === VideoRecorderStatus.Paused || s === VideoRecorderStatus.Previewing
    )
  })

  const hasStartedRecordingVideo = computed<boolean>(() => {
    return (
      hasUnsavedVideoWork.value ||
      videoRecorderStatus.value === VideoRecorderStatus.Saving ||
      videoRecorderStatus.value === VideoRecorderStatus.Submitted
    )
  })

  // #endregion Video recorder state

  // #region Close guard

  function showDiscardDialog(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title: __('You have unsaved changes'),
      body: __('Are you sure you want to discard your recording?'),
      confirmButtonText: __('Discard'),
      cancelButtonText: __('Stay here'),
      afterConfirmActions: [() => setVideoRecorderStatus(VideoRecorderStatus.Streaming)],
      afterCancelActions: [],
      shouldFadeIn: false,
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    })
  }

  // #endregion Close guard

  // #region Beforeunload
  // Watcher needed because the beforeunload listener must be managed at the store level
  // (not the component) to survive component unmount/remount cycles during recording.
  // A computed property cannot produce side effects (adding/removing event listeners).

  const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (hasUnsavedVideoWork.value) {
      event.preventDefault()
    }
  }

  unboundedWatch(hasUnsavedVideoWork, (hasWork) => {
    if (hasWork) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  })

  // #endregion Beforeunload

  return {
    videoRecorderStatus,
    setVideoRecorderStatus,
    hasUnsavedVideoWork,
    hasStartedRecordingVideo,
    showDiscardDialog,
  }
})

export { useContentPickerAvVideoRecorderStore }
